import { Injectable, Output, EventEmitter, Directive, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';

import { AppConfig } from '../../../../app.config';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { NavigationLink } from '../../models/invoicing-proposal.model';


/**
 * Invoicing proposal object returned by the API
 */
export class InvoicingProposalBasicInfo {
    Id: string;
    IwmId: number;
    Identifier: string;
    Name: string;
    Description: string;
    TaskId: string;
    CollectionInvoicingProposalId: string;
    Type: string;
    Reference: string;
    Status: string;
    StatusModifier: string;
    StatusModified: string;
    TaxType: string;
    VatPercentage: number;
    TargetDate: string;
    ReportingDate: string;
    SupplierId: string;
    SupplierName: string;
    SupplierStatus: string;
    SupplierStatusModifier: string;
    SupplierStatusModified: string;
    OrdererId: string;
    OrdererName: string;
    OrdererStatus: string;
    OrdererStatusModifier: string;
    OrdererStatusModified: string;
    PayerId: string;
    PayerName: string;
    PayerBusinessIdentifier: string;
    InvoicerId: string;
    InvoicerName: string;
    InvoicerBusinessIdentifier: string;
    PartialInvoicePercentage?: number;
    PartialInvoiceType: string;
    Archived: boolean;
    TwoStepApprovalProcess: boolean;
    CheckedDate: string;
    CheckerName: string;
    CheckerId: string;
    SchedulingStatus: string;
    Created: string;
    LastModifiedTime: string;
    LastModifierPersonFullName: string;
}

class IpBasicInfoPatch {
    Identifier: string;
    Name: string;
    Description: string;
    Type: string;
    Reference: string;
    TaxType: string;
    TargetDate: string;
    ReportingDate: string;
}

export class PartialInvoiceProperties {
    Percentage: number;
    Type: string;

    constructor(percentage: number | null, type: string) {
        this.Percentage = percentage;
        this.Type = type;
    }
}

@Directive()
@Injectable({
    providedIn: 'root'
})
export class InvoicingProposalBasicinfoService {

    private readonly config = inject(AppConfig);

    private urlIwmBackendTask: string = this.config.backendIwmUri + 'Api/';
    private urlIwmBackend: string = this.config.backendIwmUri + 'InvoicingProposalApi/';
    private urlBasicinfoSearch: string = 'InvoicingProposals?$filter=IwmId eq {0}';
    private urlBasicinfo: string = 'InvoicingProposals({0})';
    private urlTaskBasicinfo: string = 'Tasks({0})';

    private urlSetPartialInvoiceProperties: string = 'InvoicingProposals({0})/Default.SetPartialInvoiceProperties';

    // Define new invoicingProposalChanged custom event
    @Output()
    invoicingProposalChanged: EventEmitter<string> = new EventEmitter<string>();


    constructor(private http: HttpClient) { }

    /**
       * Get invoicing proposal details by GUID
       * @param {string} id GUID invoicing proposal identifier
       * @returns {Observable<InvoicingProposalBasicInfo>}
       */
    public getInvoicingProposalBasicinfo(id: string): Observable<InvoicingProposalBasicInfo> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', id);
        const ip = this.fetchData(url);
        return ip;
    }

    /**
       * Get task details by GUID
       * @param {string} id GUID task identifier
       * @returns {Observable<TaskBasicInfo>}
       */
    public getTaskBasicinfo(id: string): Observable<TaskBasicInfo> {
        const url = this.urlIwmBackendTask + this.urlTaskBasicinfo.replace('{0}', id);
        const task = this.fetchData(url);
        return task;
    }

    /**
     * Get IP basic info.
     * @param id iwm id
     */
    public getInvoicingProposalBasicInfoById(iwmId: number): Observable<InvoicingProposalBasicInfo> {
        const url = this.urlIwmBackend + this.urlBasicinfoSearch.replace('{0}', iwmId.toString());
        const data: Observable<InvoicingProposalBasicInfo> = <Observable<InvoicingProposalBasicInfo>>this.fetchData(url);
        return data;
    }

    /**
     * Update invoicing proposal
     * @param {string} Id Id of the invoicing proposal
     * @param {InvoicingProposalBasicInfo} basicInfo InvoicingProposalBasicInfo object
     * @returns {Observable<boolean>}
     */
    public update(Id: string, basicInfo: Partial<InvoicingProposalBasicInfo>): Observable<any> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', Id);
        return this.http.patch(url, basicInfo);
    }


    /**
    * Sets partial invoice properties
    * @param {PartialInvoiceProperties} properties to set
    */
    public setPartialInvoiceProperties(ipId: string, properties: PartialInvoiceProperties): Observable<any> {
        const url = this.urlIwmBackend + this.urlSetPartialInvoiceProperties.replace('{0}', ipId);
        return this.http.post(url, properties);
    }

    /**
    * Sets reporting date
    */
    public setReportingDate(ipId: string, date: string): Observable<any> {
        const url = this.urlIwmBackend + `InvoicingProposals(${ipId})/Default.SetReportingDate`;
        return this.http.post(url, { ReportingDate: date });
    }

    /**
    * Archive invoicing proposal (and catch possible error)
    * @param {string} invoicingProposalId InvoicingProposalId
    */
    public archiveInvoicingProposal(invoicingProposalId: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', invoicingProposalId) + '/Default.Archive';
        const resp = this.http.post(url, {})
            .pipe(catchError(e => of(e)));
        return resp;
    }

    /**
    * Batch archive invoicing proposals
    * @param {Array<string>} ips array of invoicing proposal ids
    */
    public batchArchiveInvoicingProposals(ips: Array<string>): Observable<Array<string>> {
        const url = this.urlIwmBackend + 'InvoicingProposals/Default.BatchArchive';
        return this.http.post<Array<string>>(url, { InvoicingProposals: ips })
            .pipe(map((x: any) => x?.value));
    }

    /**
    * Restore invoicing proposal (and catch possible error)
    * @param {string} invoicingProposalId InvoicingProposalId
    */
    public restoreInvoicingProposal(invoicingProposalId: string): Observable<any> {
        const url = this.urlIwmBackend + this.urlBasicinfo.replace('{0}', invoicingProposalId) + '/Default.Restore';
        const resp = this.http.post(url, {})
            .pipe(catchError(e => of(e)));
        return resp;
    }

    /**
    * Batch restore invoicing proposals from archive
    * @param {Array<string>} ips array of invoicing proposal ids
    */
    public batchRestoreInvoicingProposals(ips: Array<string>): Observable<Array<string>> {
        const url = this.urlIwmBackend + 'InvoicingProposals/Default.BatchRestore';
        return this.http.post<Array<string>>(url, { InvoicingProposals: ips })
            .pipe(map((x: any) => x?.value));
    }

    /**
     * Get navigation link name for Collection Ip
     * @param id
     * @returns
     */
    public GetNavigationCollIpLink(id: string): Observable<NavigationLink> {
        const url = `${this.urlIwmBackend}CollectionInvoicingProposals(${id})?$select=IwmId,Identifier,Name`;
        return this.http.get<NavigationLink>(url)
            .pipe(catchError(this.handleError(null))); // user may not have access to CollectionInvoicingProposals
    }

    /**
     * Get navigation link name for Task
     * @param id
     * @returns
     */
    public GetNavigationTaskLink(id: string): Observable<NavigationLink> {
        const url = `${this.urlIwmBackendTask}Tasks(${id})?$select=TaskId,Identifier,Name`;
        return this.http.get<NavigationLink>(url)
            .pipe(catchError(this.handleError(null))); // user may not have access to Task
    }

    /**
    * Fetch data from oData endpoints
    * @param {string} url Resource url
    * @returns {Observable<any>}
    */
    private fetchData(url: string): Observable<any> {
        return this.http.get(url);
    }

    /**
   * Handling error statuscodes
   */
    private handleError(errorResponse: any) {
        return (err: any) => {
            if (err instanceof HttpErrorResponse && (err.status === 403)) {
                return of(errorResponse);
            }
            else {
                return throwError(() => err);
            }
        }
    }

}

export class TaskBasicInfo {
    Id: string;
    TaxType: number;
}

